import React, { useState } from 'react';
import {
    Card, Table, Tag, Typography, Button, Input, Select, Segmented, Space, DatePicker,
    Drawer, Empty, Modal, Radio, Result, Descriptions, message
} from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ══════════════════════════════════════════════
//  Mock 数据
// ══════════════════════════════════════════════
// TODO: 联调时替换为接口数据

// 平台企业（扣费商务：企业ID 是否命中《设备流量商务》任一收费规则的关联企业）
const ENTERPRISES = [
    { id: 'ENT_SZ01', name: 'test-深圳芯视界科技有限公司', configured: true },
    { id: 'ENT_DG02', name: 'test-东莞康林物联网有限公司', configured: true },
    { id: 'ENT_BJ03', name: 'test-北京智联科技有限公司', configured: false },
    { id: 'ENT_HZ04', name: 'test-杭州云睿电子有限公司', configured: false },
];

const PRODUCTS = [
    { productId: 'P1001', productName: '4G云台摄像机' },
    { productId: 'P1002', productName: '室内云台摄像机' },
    { productId: 'P1003', productName: '电池摄像机' },
];

const MOCK_DATES = ['2026-07-15', '2026-08-10', '2026-08-20', '2026-09-01'];

// 设备流量：企业 / 产品 / 日期（国内=中国大陆，海外=非中国大陆汇总）
const MOCK_TRAFFIC = ENTERPRISES.flatMap((ent, ei) =>
    PRODUCTS.flatMap((p, pi) =>
        MOCK_DATES.map((date, di) => ({
            key: `${ent.id}-${p.productId}-${date}`,
            enterpriseId: ent.id,
            productId: p.productId,
            productName: p.productName,
            date,
            cn: Number((((ei + 1) * 120 + (pi + 1) * 45 + (di + 1) * 17) + (pi + 1) * 0.123).toFixed(3)),
            oversea: Number((((ei + 1) * 30 + (pi + 1) * 12 + (di + 1) * 5) + (pi + 1) * 0.456).toFixed(3)),
        }))
    )
);

// 判断当前登录角色（平台运营 / 企业）
const getIsPlatform = () => {
    try {
        const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}');
        return userInfo.role === 'platform';
    } catch (e) {
        return false;
    }
};

// 日期是否落在区间内（区间为 dayjs[] 或空=全部）
const inRange = (date, range) => {
    if (!range || !range[0] || !range[1]) return true;
    return date >= range[0].format('YYYY-MM-DD') && date <= range[1].format('YYYY-MM-DD');
};

const fmtG = (v) => `${Number(v).toFixed(3)} G`;

// 导出 Excel（支持多 sheet）；按需加载 xlsx，避免首屏体积膨胀
// TODO: 联调时替换为接口导出的文件流
const exportExcel = async (filename, sheets) => {
    const mod = await import('xlsx');
    const XLSX = mod.default || mod;
    const wb = XLSX.utils.book_new();
    sheets.forEach(({ name, rows }) => {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), name);
    });
    XLSX.writeFile(wb, filename);
};

// 时间范围展示文本
const rangeText = (range) => (range && range[0] && range[1]
    ? `${range[0].format('YYYY-MM-DD')} ~ ${range[1].format('YYYY-MM-DD')}`
    : '全部');

// 取一组记录的日期范围（最小～最大），无数据返回「全部」
const dataRangeText = (records) => {
    if (!records.length) return '全部';
    const dates = records.map(r => r.date).sort();
    return `${dates[0]} ~ ${dates[dates.length - 1]}`;
};

// ══════════════════════════════════════════════
//  主页面
// ══════════════════════════════════════════════
const DeviceTrafficStat = () => {
    const [isPlatform, setIsPlatform] = useState(getIsPlatform());

    // 一级列表筛选（草稿 + 已应用）
    const [listType, setListType] = useState('name');
    const [listValue, setListValue] = useState('');
    const [listConfigured, setListConfigured] = useState(undefined);
    const [listRange, setListRange] = useState(null); // dayjs[]
    const [appliedList, setAppliedList] = useState({ type: 'name', value: '', configured: undefined, range: null });

    // 流量明细 Drawer
    const [detailVisible, setDetailVisible] = useState(false);
    const [detailEnterprise, setDetailEnterprise] = useState(null);

    // 产品汇总筛选（草稿 + 已应用，默认继承一级列表时间）
    const [prodName, setProdName] = useState('');
    const [prodId, setProdId] = useState('');
    const [prodRange, setProdRange] = useState(null);
    const [appliedProd, setAppliedProd] = useState({ name: '', id: '', range: null });

    // Tab2 行内导出弹窗
    const [exportOpen, setExportOpen] = useState(false);
    const [exportProduct, setExportProduct] = useState(null);
    const [exportMode, setExportMode] = useState('all');
    const [exportMonths, setExportMonths] = useState(null);

    const handleRoleChange = (val) => {
        setIsPlatform(val === 'platform');
    };

    // ── 一级列表 ──
    const handleListSearch = () => {
        setAppliedList({
            type: listType,
            value: listValue.trim(),
            configured: listConfigured,
            range: listRange || null,
        });
    };
    const handleListReset = () => {
        setListType('name');
        setListValue('');
        setListConfigured(undefined);
        setListRange(null);
        setAppliedList({ type: 'name', value: '', configured: undefined, range: null });
    };

    // 仅展示有流量数据的企业；再叠加筛选条件
    const enterpriseRows = ENTERPRISES
        .filter(ent => MOCK_TRAFFIC.some(t => t.enterpriseId === ent.id))
        .map(ent => {
            const rows = MOCK_TRAFFIC.filter(t => t.enterpriseId === ent.id && inRange(t.date, appliedList.range));
            const cn = rows.reduce((s, r) => s + r.cn, 0);
            const oversea = rows.reduce((s, r) => s + r.oversea, 0);
            return { key: ent.id, ...ent, cn, oversea };
        })
        .filter(r => {
            if (appliedList.value) {
                if (appliedList.type === 'name' && !r.name.includes(appliedList.value)) return false;
                if (appliedList.type === 'id' && !r.id.includes(appliedList.value)) return false;
            }
            if (appliedList.configured && appliedList.configured !== (r.configured ? 'yes' : 'no')) return false;
            return true;
        });

    const listColumns = [
        { title: '企业信息', key: 'enterprise', render: (_, r) => (
            <div><div style={{ fontWeight: 500 }}>{r.name}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.id}</Text></div>
        )},
        { title: '扣费商务', dataIndex: 'configured', key: 'configured', align: 'center', render: v => (
            v
                ? <Tag color="success" style={{ border: 'none' }}>已配置</Tag>
                : <Tag color="default" style={{ border: 'none' }}>未配置</Tag>
        )},
        { title: '国内流量', dataIndex: 'cn', key: 'cn', align: 'right', render: v => fmtG(v) },
        { title: '海外流量', dataIndex: 'oversea', key: 'oversea', align: 'right', render: v => fmtG(v) },
        { title: '操作', key: 'action', width: 110, fixed: 'right', render: (_, r) => (
            <Button type="link" size="small" onClick={() => handleOpenDetail(r)}>流量明细</Button>
        )},
    ];

    // ── 流量明细 ──
    const handleOpenDetail = (record) => {
        const inheritedRange = appliedList.range || null; // 顶部汇总与产品汇总时间默认继承一级列表
        setDetailEnterprise(record);
        setProdName('');
        setProdId('');
        setProdRange(inheritedRange);
        setAppliedProd({ name: '', id: '', range: inheritedRange });
        setDetailVisible(true);
    };
    const handleDetailClose = () => {
        setDetailVisible(false);
        setDetailEnterprise(null);
    };

    // 顶部：国内外汇总（时间继承一级列表）
    const summaryRows = detailEnterprise ? MOCK_TRAFFIC.filter(t => t.enterpriseId === detailEnterprise.id && inRange(t.date, appliedList.range)) : [];
    const summaryCn = summaryRows.reduce((s, r) => s + r.cn, 0);
    const summaryOversea = summaryRows.reduce((s, r) => s + r.oversea, 0);

    // 产品汇总
    const handleProdSearch = () => {
        setAppliedProd({
            name: prodName.trim(),
            id: prodId.trim(),
            range: prodRange || null,
        });
    };
    const handleProdReset = () => {
        const inheritedRange = appliedList.range || null; // 重置恢复为继承一级列表时间
        setProdName('');
        setProdId('');
        setProdRange(inheritedRange);
        setAppliedProd({ name: '', id: '', range: inheritedRange });
    };

    const productRows = detailEnterprise ? PRODUCTS
        .filter(p => MOCK_TRAFFIC.some(t => t.enterpriseId === detailEnterprise.id && t.productId === p.productId))
        .map(p => {
            const rows = MOCK_TRAFFIC.filter(t => t.enterpriseId === detailEnterprise.id && t.productId === p.productId && inRange(t.date, appliedProd.range));
            return {
                key: p.productId,
                productId: p.productId,
                productName: p.productName,
                cn: rows.reduce((s, r) => s + r.cn, 0),
                oversea: rows.reduce((s, r) => s + r.oversea, 0),
            };
        })
        .filter(r => r.cn + r.oversea > 0)
        .filter(r => {
            if (appliedProd.name && !r.productName.includes(appliedProd.name)) return false;
            if (appliedProd.id && !r.productId.includes(appliedProd.id)) return false;
            return true;
        }) : [];

    const productColumns = [
        { title: '产品信息', key: 'product', render: (_, r) => (
            <div><div style={{ fontWeight: 500 }}>{r.productName}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.productId}</Text></div>
        )},
        { title: '国内流量(G)', dataIndex: 'cn', key: 'cn', align: 'right', render: v => Number(v).toFixed(3) },
        { title: '海外流量(G)', dataIndex: 'oversea', key: 'oversea', align: 'right', render: v => Number(v).toFixed(3) },
        { title: '操作', key: 'action', width: 100, fixed: 'right', render: (_, r) => (
            <Button type="link" size="small" icon={<DownloadOutlined />} onClick={() => handleOpenExport(r)}>导出</Button>
        )},
    ];

    // ── 导出（mock：生成 Excel 直接下载） ──
    const today = dayjs().format('YYYYMMDD');

    // 1. 一级企业列表导出（单 sheet）
    const handleListExport = async () => {
        const rows = [
            ['企业名称', '企业ID', '扣费商务', '国内消耗(G)', '海外消耗(G)', '时间范围'],
            ...enterpriseRows.map(r => [
                r.name, r.id, r.configured ? '已配置' : '未配置',
                Number(r.cn).toFixed(3), Number(r.oversea).toFixed(3), rangeText(appliedList.range),
            ]),
        ];
        await exportExcel(`设备流量统计_企业流量_${today}.xlsx`, [{ name: '企业流量', rows }]);
        message.success('导出成功');
    };

    // 2. 产品汇总工具栏导出（按查询条件，多产品；sheet1 汇总 + sheet2 日明细）
    const handleProductExport = async () => {
        if (!detailEnterprise || productRows.length === 0) {
            message.warning('无可导出的产品流量数据');
            return;
        }
        const summaryRows = [['产品名称', '产品ID', '国内消耗(G)', '海外消耗(G)', '时间范围']];
        const detailRows = [['产品名称', '产品ID', '日期', '国内消耗(G)', '海外消耗(G)']];
        productRows.forEach(p => {
            const records = MOCK_TRAFFIC
                .filter(t => t.enterpriseId === detailEnterprise.id && t.productId === p.productId && inRange(t.date, appliedProd.range))
                .sort((a, b) => (a.date > b.date ? 1 : -1));
            summaryRows.push([
                p.productName, p.productId, Number(p.cn).toFixed(3), Number(p.oversea).toFixed(3),
                appliedProd.range && appliedProd.range[0] && appliedProd.range[1] ? rangeText(appliedProd.range) : dataRangeText(records),
            ]);
            records.forEach(d => detailRows.push([
                p.productName, p.productId, d.date, Number(d.cn).toFixed(3), Number(d.oversea).toFixed(3),
            ]));
        });
        await exportExcel(`产品流量_${detailEnterprise.name}_${today}.xlsx`, [
            { name: '产品汇总', rows: summaryRows },
            { name: '产品日明细', rows: detailRows },
        ]);
        message.success('导出成功');
    };

    // 3. 产品汇总行内导出（单产品，弹窗月份区间）
    const handleOpenExport = (record) => {
        setExportProduct(record);
        setExportMode('all');
        setExportMonths(null);
        setExportOpen(true);
    };
    const handleExportConfirm = async () => {
        if (exportMode === 'custom' && (!exportMonths || !exportMonths[0] || !exportMonths[1])) {
            message.warning('请选择月份区间');
            return;
        }
        if (!detailEnterprise || !exportProduct) return;
        const monthStart = exportMode === 'custom' ? exportMonths[0].format('YYYY-MM') : null;
        const monthEnd = exportMode === 'custom' ? exportMonths[1].format('YYYY-MM') : null;
        const records = MOCK_TRAFFIC
            .filter(t => t.enterpriseId === detailEnterprise.id && t.productId === exportProduct.productId)
            .filter(t => (monthStart ? (t.date.slice(0, 7) >= monthStart && t.date.slice(0, 7) <= monthEnd) : true))
            .sort((a, b) => (a.date > b.date ? 1 : -1));
        const cn = records.reduce((s, r) => s + r.cn, 0);
        const oversea = records.reduce((s, r) => s + r.oversea, 0);
        const timeRangeText = exportMode === 'custom' ? `${monthStart} ~ ${monthEnd}` : dataRangeText(records);
        const summaryRows = [
            ['产品名称', '产品ID', '国内消耗(G)', '海外消耗(G)', '时间范围'],
            [exportProduct.productName, exportProduct.productId, cn.toFixed(3), oversea.toFixed(3), timeRangeText],
        ];
        const detailRows = [
            ['产品名称', '产品ID', '日期', '国内消耗(G)', '海外消耗(G)'],
            ...records.map(d => [exportProduct.productName, exportProduct.productId, d.date, Number(d.cn).toFixed(3), Number(d.oversea).toFixed(3)]),
        ];
        await exportExcel(`${exportProduct.productName}_流量_${today}.xlsx`, [
            { name: '产品汇总', rows: summaryRows },
            { name: '产品日明细', rows: detailRows },
        ]);
        setExportOpen(false);
        message.success('导出成功');
    };

    // 企业侧不可访问：仅平台角色
    if (!isPlatform) {
        return (
            <div style={{ padding: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Title level={4} style={{ margin: 0 }}>设备流量统计</Title>
                    <Space>
                        <Text type="secondary" style={{ fontSize: 12 }}>mock 角色切换</Text>
                        <Segmented
                            options={[{ label: '平台角色', value: 'platform' }, { label: '企业角色', value: 'enterprise' }]}
                            value={isPlatform ? 'platform' : 'enterprise'}
                            onChange={handleRoleChange}
                        />
                    </Space>
                </div>
                <Card bordered={false} style={{ borderRadius: 8 }}>
                    <Result status="403" title="无访问权限" subTitle="「设备流量统计」仅平台角色可访问，请切换到平台角色查看。" />
                </Card>
            </div>
        );
    }

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={4} style={{ marginBottom: 0 }}>设备流量统计</Title>
                <Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>mock 角色切换</Text>
                    <Segmented
                        options={[{ label: '平台角色', value: 'platform' }, { label: '企业角色', value: 'enterprise' }]}
                        value={isPlatform ? 'platform' : 'enterprise'}
                        onChange={handleRoleChange}
                    />
                </Space>
            </div>

            <Card bordered={false} style={{ borderRadius: 8, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <Space.Compact>
                        <Select
                            style={{ width: 110 }}
                            value={listType}
                            onChange={(v) => { setListType(v); setListValue(''); }}
                            options={[{ value: 'name', label: '企业名称' }, { value: 'id', label: '企业ID' }]}
                        />
                        <Input
                            style={{ width: 260 }}
                            allowClear
                            placeholder={listType === 'name' ? '请输入企业名称' : '请输入企业ID'}
                            value={listValue}
                            onChange={e => setListValue(e.target.value)}
                        />
                    </Space.Compact>
                    <Select
                        style={{ width: 140 }}
                        allowClear
                        placeholder="扣费商务：全部"
                        value={listConfigured}
                        onChange={setListConfigured}
                        options={[{ value: 'yes', label: '已配置' }, { value: 'no', label: '未配置' }]}
                    />
                    <RangePicker value={listRange} onChange={setListRange} />
                    <Button type="primary" icon={<SearchOutlined />} onClick={handleListSearch}>查询</Button>
                    <Button onClick={handleListReset}>重置</Button>
                </div>
            </Card>

            <Card bordered={false} style={{ borderRadius: 8 }}
                title={<Text strong style={{ fontSize: 16 }}>企业流量</Text>}
                extra={<Button icon={<DownloadOutlined />} onClick={handleListExport}>导出</Button>}
            >
                <Table columns={listColumns} dataSource={enterpriseRows} rowKey="key"
                    pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }} />
            </Card>

            {/* 流量明细 Drawer：国内外汇总 / 产品汇总 双 Tab */}
            <Drawer
                title="流量明细"
                open={detailVisible}
                onClose={handleDetailClose}
                width="70%"
            >
                {detailEnterprise && (
                    <>
                        <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
                            <Descriptions.Item label="企业名称">{detailEnterprise.name}</Descriptions.Item>
                            <Descriptions.Item label="企业ID">{detailEnterprise.id}</Descriptions.Item>
                            <Descriptions.Item label="扣费商务" span={2}>
                                {detailEnterprise.configured
                                    ? <Tag color="success" style={{ border: 'none' }}>已配置</Tag>
                                    : <Tag color="default" style={{ border: 'none' }}>未配置</Tag>}
                            </Descriptions.Item>
                            <Descriptions.Item label="中国大陆(CN) 流量消耗(G)">{summaryCn.toFixed(3)}</Descriptions.Item>
                            <Descriptions.Item label="海外(EN) 消耗流量(G)">{summaryOversea.toFixed(3)}</Descriptions.Item>
                        </Descriptions>

                        <div style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
                            <Text strong style={{ fontSize: 15 }}>产品汇总</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                            <Input
                                style={{ width: 200 }}
                                allowClear
                                placeholder="请输入产品名称"
                                value={prodName}
                                onChange={e => setProdName(e.target.value)}
                            />
                            <Input
                                style={{ width: 180 }}
                                allowClear
                                placeholder="请输入产品ID"
                                value={prodId}
                                onChange={e => setProdId(e.target.value)}
                            />
                            <RangePicker value={prodRange} onChange={setProdRange} />
                            <Button type="primary" icon={<SearchOutlined />} onClick={handleProdSearch}>查询</Button>
                            <Button onClick={handleProdReset}>重置</Button>
                            <Button icon={<DownloadOutlined />} onClick={handleProductExport}>导出</Button>
                        </div>
                        {productRows.length === 0 ? (
                            <Empty description="暂无产品流量数据" style={{ marginTop: 40 }} />
                        ) : (
                            <Table size="small" columns={productColumns} dataSource={productRows} rowKey="key"
                                pagination={false} />
                        )}
                    </>
                )}
            </Drawer>

            {/* 产品汇总行内导出弹窗：选择月份区间（默认全部） */}
            <Modal
                title={`导出产品流量${exportProduct ? ` - ${exportProduct.productName}` : ''}`}
                open={exportOpen}
                onCancel={() => setExportOpen(false)}
                onOk={handleExportConfirm}
                okText="导出"
                cancelText="取消"
                width={520}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">导出范围</Text>
                    <Radio.Group
                        style={{ display: 'block', marginTop: 8 }}
                        value={exportMode}
                        onChange={e => setExportMode(e.target.value)}
                    >
                        <Radio value="all">全部</Radio>
                        <Radio value="custom">按月选择</Radio>
                    </Radio.Group>
                </div>
                {exportMode === 'custom' && (
                    <RangePicker
                        picker="month"
                        style={{ width: '100%' }}
                        value={exportMonths}
                        onChange={setExportMonths}
                        placeholder={['开始月份', '结束月份']}
                    />
                )}
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 12 }}>
                    选「全部」时，导出汇总 sheet 的时间范围取该产品数据的最小日期 ～ 最大日期。
                </Text>
            </Modal>
        </div>
    );
};

export default DeviceTrafficStat;
